import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import withHostContext, {
  trackClickTypes,
  trackingLocations,
  trackView,
} from '../../context/host/consumer';

import Icon from '../Icon';
import Tooltip from '../Tooltip';
import './styles.css';
import { ReactComponent as FileLikedSvg } from './file-liked.svg';

const messages = defineMessages({
  removeFromCITA: {
    id: 'hubs.overview.fav-indicator.remove-from-cita',
    defaultMessage: `Remove from Colleges I'm Thinking About`,
  },
  addToCITA: {
    id: 'hubs.overview.fav-indicator.add-to-cita',
    defaultMessage: `Add to Colleges I'm Thinking About`,
  },
  removeFromFavorites: {
    id: 'hubs.overview.fav-indicator.remove-from-fav',
    defaultMessage: `Remove from favorites`,
  },
  addToFavorites: {
    id: 'hubs.overview.fav-indicator.add-to-fav',
    defaultMessage: `Add to favorites`,
  },
  clickToUpdate: {
    id: 'hubs.overview.fav-indicator.click-to-update',
    defaultMessage: `It looks like you're planning to apply to this college! Click to update status.`,
  },
  applyingTo: {
    id: 'hubs.overview.fav-indicator.applying-to',
    defaultMessage: ` Applying To`,
  },
  updateStatus: {
    id: 'hubs.overview.fav-indicator.update-status',
    defaultMessage: ` Update Status`,
  },
});

function getCollegeApplyingToLink(ceebCode) {
  return `${window.origin}/colleges/application/edit?cid=${ceebCode}#${ceebCode}`;
}

class FavoriteIndicator extends React.PureComponent {
  static propTypes = {
    isFavorite: PropTypes.bool,
    toggleFavorite: PropTypes.func,
    isApplyingTo: PropTypes.bool,
    ceebCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    intl: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.state = {
      lastToggledValue: null,
    };
  }

  handleToggleFavorite = () => {
    const { toggleFavorite, isFavorite } = this.props;
    const isCamino = get(this.props, 'hostContext.isCamino', false);
    const { lastToggledValue } = this.state;
    const favorite = lastToggledValue !== null ? lastToggledValue : isFavorite;

    // Camino requires some more functionality (e.g disconnecting) which does not work well with
    // changing the toggle state locally in this component
    // Now, for Camino the toggle state will be changed from the parent application
    // Camino currently does not have tracking
    if (isCamino) {
      toggleFavorite(!favorite);
    } else {
      toggleFavorite(!favorite);
      trackView(this.props.hostContext, {
        type: trackClickTypes.HUBS_VIEW,
        location: favorite ? trackingLocations.UNFAVORITE : trackingLocations.FAVORITE,
      });
      this.setState({
        lastToggledValue: !favorite,
      });
    }
  };

  getTooltipMessage(favorite) {
    const isCamino = get(this.props, 'hostContext.isCamino', false);
    if (isCamino) {
      return favorite ? messages.removeFromFavorites : messages.addToFavorites;
    } else {
      return favorite ? messages.removeFromCITA : messages.addToCITA;
    }
  }

  render() {
    const { isApplyingTo, isFavorite, ceebCode, toggleFavorite, intl } = this.props;
    const { lastToggledValue } = this.state;
    const favorite = lastToggledValue !== null ? lastToggledValue : isFavorite;

    return (
      <React.Fragment>
        {!isApplyingTo && favorite !== undefined && (
          <React.Fragment>
            {toggleFavorite ? (
              <Tooltip
                contentText={intl.formatMessage(this.getTooltipMessage(favorite))}
                showOnHover
              >
                <Icon
                  className={favorite ? '' : 'empty-heart-icon'}
                  name={favorite ? 'heart' : 'heart outline'}
                  role="button"
                  tabIndex="0"
                  aria-label={favorite ? 'unfavorite' : 'favorite'}
                  aria-hidden="false"
                  onClick={this.handleToggleFavorite}
                />
              </Tooltip>
            ) : (
              <Icon
                className={favorite ? '' : 'empty-heart-icon'}
                name={favorite ? 'heart' : 'heart outline'}
                alt={favorite ? 'favorite' : 'not favorite'}
              />
            )}
          </React.Fragment>
        )}
        {isApplyingTo && (
          <Tooltip contentText={intl.formatMessage(messages.clickToUpdate)} showOnHover>
            <a
              className="applying-to-icon-link"
              href={getCollegeApplyingToLink(ceebCode)}
              target="_blank"
              rel="opener"
            >
              <div className="applying-to-icon">
                <FileLikedSvg className="file-liked-icon" />
                <span className="applying-to-text">
                  <FormattedMessage {...messages.applyingTo} />
                </span>
                <span className="update-status-text">
                  <FormattedMessage {...messages.updateStatus} />
                </span>
              </div>
            </a>
          </Tooltip>
        )}
      </React.Fragment>
    );
  }
}

export default injectIntl(withHostContext(FavoriteIndicator));
